import React from 'react';
import { X, Printer, FileText } from 'lucide-react';
import { PAPER_DIMENSIONS, PaperSize } from '../utils/pdfExportUtil';

interface HtmlPrintPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  elementId: string;
  documentTitle: string;
  paperSize?: PaperSize;
}

export const HtmlPrintPreviewModal: React.FC<HtmlPrintPreviewModalProps> = ({
  isOpen,
  onClose,
  elementId,
  documentTitle,
  paperSize = 'a4'
}) => {
  if (!isOpen) return null;

  const sourceEl = document.getElementById(elementId);
  const paperConfig = PAPER_DIMENSIONS[paperSize] || PAPER_DIMENSIONS.a4;
  const isEcoBw = sourceEl?.classList.contains('bg-white') || sourceEl?.className.includes('bg-white');
  const winBg = isEcoBw ? '#FFFFFF' : '#FFFDF7';
  const winColor = isEcoBw ? '#000000' : '#263332';

  const handlePrint = () => {
    window.print();
  };

  const styleTags = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
    .map(el => el.outerHTML)
    .join('\n');

  const contentHtml = sourceEl ? sourceEl.outerHTML : '<p>Report content not found.</p>';

  const fullHtml = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>${documentTitle}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Cormorant+Garamond:ital,wght@0,600;0,700;1,600&family=Cinzel:wght@600;700&display=swap" rel="stylesheet">
  ${styleTags}
  <style>
    body {
      background-color: ${winBg} !important;
      color: ${winColor} !important;
      margin: 0;
      padding: 24px;
      display: flex;
      justify-content: center;
      font-family: 'Plus Jakarta Sans', system-ui, -apple-system, sans-serif;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    @page {
      size: ${paperConfig.format} portrait;
      margin: 8mm 10mm 8mm 10mm;
    }
    @media print {
      body {
        padding: 0 !important;
      }
      .no-print-element {
        display: none !important;
      }
    }
  </style>
</head>
<body>
  <div style="width: 100%; max-width: 850px;">
    ${contentHtml}
  </div>
</body>
</html>`;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-black/80 backdrop-blur-sm flex flex-col">
      {/* Top Tool Bar */}
      <div className="bg-[#174C4B] text-[#FFFDF7] px-6 py-3.5 border-b border-[#B58A4A]/40 flex items-center justify-between shadow-lg shrink-0">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#B58A4A]/20 rounded-xl border border-[#B58A4A]">
            <FileText className="w-5 h-5 text-[#B58A4A]" />
          </div>
          <div>
            <h2 className="text-sm font-bold font-serif tracking-wide">{documentTitle}</h2>
            <p className="text-[11px] text-[#E8F0EF]/80 font-mono">
              In-App HTML Print Tool • {paperConfig.label}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-[#B58A4A] hover:bg-[#967035] text-white rounded-xl text-xs font-bold shadow-md transition-all cursor-pointer"
          >
            <Printer className="w-4 h-4" />
            <span>Print / Save as PDF</span>
          </button>
          <button
            onClick={onClose}
            className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all cursor-pointer"
            title="Close Preview"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* HTML Preview Iframe Container */}
      <div className="flex-1 overflow-auto p-6 bg-gray-900/60 flex justify-center items-start">
        <div className="bg-white shadow-2xl rounded-2xl overflow-hidden w-full max-w-[850px] min-h-[900px]">
          <iframe
            srcDoc={fullHtml}
            title={documentTitle}
            className="w-full h-[1100px] border-0"
          />
        </div>
      </div>
    </div>
  );
};
